import {
  Factory,
  Model,
  Response,
  type Collection,
  type ModelInstance,
} from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { getTodayDateTimeISO } from '@/utils/date/getter.js'
import {
  randFullName,
  randPastDate,
  randRecentDate,
  randNumber,
  randBetweenDate,
  randChanceBoolean,
} from '@ngneat/falso'

import type { SchemaAttributes } from '../types'
import { sortable, pageable, filterable, createErrorResponse } from './utils.js'
import type { MirageServer, MockServer } from './server'

export type ItemCategoryData = SchemaAttributes<'ItemCategory'>

export const model = Model as ModelDefinition<ItemCategoryData>

export const factory = Factory.extend({
  name() {
    return randFullName()
  },
  created() {
    return randPastDate({ years: 1 }).toISOString()
  },
  modified() {
    return randBetweenDate({
      from: this.created as string,
      to: randRecentDate(),
    }).toISOString()
  },
  items_count() {
    return randNumber({ max: 28 })
  },
  archived() {
    return randChanceBoolean({ chanceTrue: 0.2 })
  },
})

export function routes(server: MockServer) {
  /**
   * GET /item_categories
   */
  server.get(
    '/item_categories',
    function (this: MirageServer, schema, request) {
      const allCategories = schema.all('itemCategory') as Collection<
        ModelInstance<ItemCategoryData>
      >

      // 1) Sort
      const sorted = sortable(allCategories, request)

      // 2) Filter using the established utility
      const filtered = filterable(sorted, request, {
        'name.icontains': (item, value) =>
          item.attrs.name.toLowerCase().includes(value.toLowerCase()),
        archived: (item, value) => item.attrs.archived === (value === 'true'),
      })

      // 3) Paginate
      const { data, meta } = pageable(filtered, request)

      return {
        data: data.models.map((itemCategory) => {
          return {
            id: itemCategory.id,
            type: 'ItemCategory',
            attributes: {
              name: itemCategory.attrs.name,
              created: itemCategory.attrs.created,
              modified: itemCategory.attrs.modified,
              items_count: itemCategory.attrs.items_count,
              archived: itemCategory.attrs.archived,
            },
          }
        }),
        meta: {
          pagination: meta,
        },
      }
    },
  )

  /**
   * GET /item_categories/:id
   */
  server.get('/item_categories/:id', (schema, request) => {
    const id = request.params.id
    const itemCategory = schema.find(
      'itemCategory',
      id,
    ) as ModelInstance<ItemCategoryData> | null

    if (!itemCategory) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `ItemCategory with id ${id} not found` }],
      })
    }

    return {
      data: {
        id: itemCategory.id,
        type: 'ItemCategory',
        attributes: {
          name: itemCategory.attrs.name,
          created: itemCategory.attrs.created,
          modified: itemCategory.attrs.modified,
          items_count: itemCategory.attrs.items_count,
          archived: itemCategory.attrs.archived,
        },
      },
    }
  })

  /**
   * POST /item_categories
   */
  server.post('/item_categories', (schema, request) => {
    const attrs = JSON.parse(request.requestBody)

    if (!attrs?.data?.attributes?.name) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "name" in request body' }],
      })
    }

    const { id, attributes } = attrs.data
    const createdRecord = schema.create('itemCategory', {
      id: id || undefined, // let Mirage generate one if not provided
      name: attributes.name,
      created: randPastDate().toISOString(),
      modified: getTodayDateTimeISO(),
      items_count: randNumber({ max: 100 }),
      archived: false,
    })

    return {
      data: {
        id: createdRecord.id,
        type: 'ItemCategory',
        attributes: {
          name: createdRecord.attrs.name,
          created: createdRecord.attrs.created,
          modified: createdRecord.attrs.modified,
          items_count: createdRecord.attrs.items_count,
          archived: createdRecord.attrs.archived,
        },
      },
    }
  })

  /**
   * PATCH /item_categories/:id
   */
  server.patch('/item_categories/:id', (schema, request) => {
    const id = request.params.id
    const attrs = JSON.parse(request.requestBody)
    const itemCategory = schema.find(
      'itemCategory',
      id,
    ) as ModelInstance<ItemCategoryData> | null

    if (!itemCategory) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `ItemCategory with id ${id} not found` }],
      })
    }

    if (!attrs?.data?.attributes) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "attributes" in request body' }],
      })
    }

    const { name, archived } = attrs.data.attributes

    itemCategory.update({
      name: name ?? itemCategory.attrs.name,
      archived: archived ?? itemCategory.attrs.archived,
      modified: getTodayDateTimeISO(),
    })

    return {
      data: {
        id: itemCategory.id,
        type: 'ItemCategory',
        attributes: {
          name: itemCategory.attrs.name,
          created: itemCategory.attrs.created,
          modified: itemCategory.attrs.modified,
          items_count: itemCategory.attrs.items_count,
          archived: itemCategory.attrs.archived,
        },
      },
    }
  })

  server.delete('/item_categories/:id', (schema, request) => {
    const id = request.params.id
    const itemCategory = schema.find(
      'itemCategory',
      id,
    ) as ModelInstance<ItemCategoryData> | null

    if (!itemCategory) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `ItemCategory with id ${id} not found` }],
      })
    }

    itemCategory.update({
      archived: true,
      modified: getTodayDateTimeISO(),
    })

    return new Response(204, {}, '')
  })
}

export function seeds(server: MockServer) {
  server.createList('itemCategory', 50)
}
